import { motion } from 'framer-motion';
import { Check, MapPin } from 'lucide-react';
import type { ContactData, SocialData } from './sitePageTypes';

interface Props {
  contact: ContactData;
  social: SocialData;
  hasOfficesType: boolean | null;
  saving: boolean;
  settingsReady: boolean;
  onManageOffices: () => void;
  setContact: (contact: ContactData) => void;
  setSocial: (social: SocialData) => void;
  onSave: () => void;
}

const contactFields: { key: 'email' | 'phone' | 'whatsapp' | 'address' | 'hours'; label: string; placeholder: string }[] = [
  { key: 'email', label: 'Email', placeholder: 'hola@empresa.com' },
  { key: 'phone', label: 'Teléfono', placeholder: '+52 664 000 0000' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '+52 664 000 0000 (si difiere del teléfono)' },
  { key: 'address', label: 'Dirección', placeholder: 'Calle, Ciudad, País' },
  { key: 'hours', label: 'Horario', placeholder: 'Lun–Vie 9:00–18:00' },
];

const socialFields: { key: keyof SocialData; label: string; placeholder: string }[] = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/...' },
  { key: 'twitter', label: 'X / Twitter', placeholder: 'https://twitter.com/...' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...' },
];

export default function SiteContactTab({
  contact,
  social,
  hasOfficesType,
  saving,
  settingsReady,
  onManageOffices,
  setContact,
  setSocial,
  onSave,
}: Props) {
  return (
    <div className="space-y-4">
      {hasOfficesType !== null && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className={`flex gap-3 rounded-lg border px-3.5 py-3.5 ${hasOfficesType ? 'bg-ui-accent/8 border-ui-accent/25' : 'bg-ui-surface border-ui-border'}`}
        >
          {hasOfficesType
            ? <Check size={14} className="text-ui-accent shrink-0 mt-0.5" />
            : <MapPin size={14} className="text-ui-text-subtle shrink-0 mt-0.5" />}
          <div className="space-y-1 min-w-0">
            {hasOfficesType ? (
              <>
                <p className="text-sm font-medium text-ui-text">Sucursales activas</p>
                <p className="text-xs text-ui-text-subtle leading-relaxed">
                  Estos datos son el <strong className="text-ui-text-muted">punto de partida general</strong>. Cada sucursal puede definir su propio teléfono, email y horario.
                </p>
                <button type="button" onClick={onManageOffices} className="text-xs text-ui-accent hover:opacity-80 transition-opacity mt-0.5">
                  Gestionar sucursales →
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-ui-text">Solo datos globales de contacto</p>
                <p className="text-xs text-ui-text-subtle leading-relaxed">
                  Si tu negocio tiene más de una sede, activa <strong className="text-ui-text-muted">Sucursales</strong> para gestionar dirección, mapa y horario por ubicación.
                </p>
              </>
            )}
          </div>
        </motion.div>
      )}

      {contactFields.map(({ key, label, placeholder }) => (
        <TextField
          key={key}
          label={label}
          value={contact[key]}
          placeholder={placeholder}
          onChange={(value) => setContact({ ...contact, [key]: value })}
        />
      ))}

      <div className="pt-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={contact.whatsappFab !== false}
            onChange={(event) => setContact({ ...contact, whatsappFab: event.target.checked })}
            className="rounded border-ui-border"
          />
          <span className="text-sm text-ui-text-muted">Mostrar botón flotante de WhatsApp</span>
        </label>
      </div>

      <div>
        <label className="block text-sm text-ui-text-muted mb-1">Mensaje pre-escrito de WhatsApp</label>
        <textarea
          value={contact.whatsappMessage ?? ''}
          onChange={(event) => setContact({ ...contact, whatsappMessage: event.target.value })}
          placeholder="Hola, vi su sitio y quiero más información sobre…"
          rows={2}
          className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent resize-none"
        />
      </div>

      <div className="pt-4 border-t border-ui-border space-y-4">
        <p className="text-xs text-ui-text-muted uppercase tracking-wider">Redes sociales</p>
        {socialFields.map(({ key, label, placeholder }) => (
          <TextField
            key={key}
            label={label}
            value={social[key]}
            placeholder={placeholder}
            onChange={(value) => setSocial({ ...social, [key]: value })}
          />
        ))}
      </div>

      <div className="pt-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !settingsReady}
          className="px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90"
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm text-ui-text-muted mb-1">{label}</label>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
      />
    </div>
  );
}
